"use client";

import { useState, useEffect } from "react";
import { Button } from "../ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { KeyValueEditor } from "../ui/KeyValueEditor";
import { PRIMARY } from "../../lib/buttonStyles";
import {
  AUTH_TYPES,
  emptyApiCredentialForm,
  formFromApiCredential,
  validateApiCredentialForm,
  reusableSavedAuth,
} from "./apiCredentialForm";

function Field({ id, label, value, onChange, placeholder, type = "text", className = "", required = false, disabled = false, hint }) {
  return (
    <div className={`space-y-2 ${className}`}>
      <Label htmlFor={id} className="text-gray-300 font-medium text-sm">{label}</Label>
      <Input
        id={id} type={type} value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        autoComplete="off"
        className="bg-gray-800 border border-gray-700 text-white placeholder-gray-400 rounded-xl h-11
                   focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-all duration-300"
      />
      {hint && <p className="text-xs text-gray-500">{hint}</p>}
    </div>
  );
}

/**
 * Alta / edición de una credencial tipo "api".
 * `connection` es la credencial completa (GET) cuando se edita, o null.
 * Los secretos nunca se vuelven a mostrar: vacíos en edición = conservar el guardado.
 */
export function ApiConnectionDialog({ open, onOpenChange, connection, onSave, saving = false, saveError = null }) {
  const [form, setForm] = useState(emptyApiCredentialForm());
  const [error, setError] = useState(null);

  useEffect(() => {
    setForm(connection ? formFromApiCredential(connection) : emptyApiCredentialForm());
    setError(null);
  }, [connection, open]);

  const set = (field) => (value) => setForm((f) => ({ ...f, [field]: value }));
  const editing = !!connection;
  const keepsSecret = !!reusableSavedAuth(form, connection);
  const secretHint = keepsSecret ? "Leave empty to keep the saved value" : undefined;
  const secretPlaceholder = keepsSecret ? "••••••••" : "";

  const handleSubmit = (e) => {
    e.preventDefault();
    if (saving) return;
    const message = validateApiCredentialForm(form, connection);
    if (message) {
      setError(message);
      return;
    }
    setError(null);
    onSave(form);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto bg-black border border-gray-800 rounded-2xl shadow-2xl backdrop-blur-3xl">
        <DialogHeader className="space-y-3 pb-4">
          <DialogTitle className="text-2xl font-bold text-purple-400">
            {editing ? "Edit API Connection" : "Add API Connection"}
          </DialogTitle>
          <DialogDescription className="text-gray-400 text-base">
            Base URL and authentication for an external API. API Call steps that use it only set the path.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field id="api-name" label="Name" value={form.name} onChange={set("name")}
              placeholder="Grab&Eat staging – reports" required disabled={saving} />
            <Field id="api-timeout" label="Timeout (s)" type="number" value={form.timeout}
              onChange={set("timeout")} disabled={saving} />
          </div>

          <Field id="api-base-url" label="Base URL" value={form.base_url} onChange={set("base_url")}
            placeholder="https://staging.grabandeat.net/api/v2" required disabled={saving}
            hint="Steps append their path to this URL, e.g. reports/sales-sensors/events" />

          <Field id="api-description" label="Description (optional)" value={form.description}
            onChange={set("description")} disabled={saving} />

          <div className="space-y-2">
            <Label className="text-gray-300 font-medium text-sm">Authentication</Label>
            <Select value={form.auth_type} onValueChange={set("auth_type")} disabled={saving}>
              <SelectTrigger className="bg-gray-800 border border-gray-700 text-white rounded-xl h-11 focus:border-purple-500">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-gray-800 border border-gray-700 rounded-xl">
                {AUTH_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {form.auth_type === "bearer_token" && (
            <Field id="api-token" label="Token" type="password" value={form.token} onChange={set("token")}
              placeholder={secretPlaceholder} hint={secretHint || "Sent as Authorization: Bearer <token>"} disabled={saving} />
          )}

          {form.auth_type === "api_key" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field id="api-header-name" label="Header name" value={form.header_name}
                onChange={set("header_name")} placeholder="X-API-Key" disabled={saving} />
              <Field id="api-key" label="API key" type="password" value={form.api_key} onChange={set("api_key")}
                placeholder={secretPlaceholder} hint={secretHint} disabled={saving} />
            </div>
          )}

          {form.auth_type === "oauth2" && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field id="api-client-id" label="Client ID" value={form.client_id} onChange={set("client_id")} disabled={saving} />
                <Field id="api-client-secret" label="Client secret" type="password" value={form.client_secret}
                  onChange={set("client_secret")} placeholder={secretPlaceholder} hint={secretHint} disabled={saving} />
              </div>
              <Field id="api-access-token" label="Access token" type="password" value={form.access_token}
                onChange={set("access_token")} placeholder={secretPlaceholder}
                hint={secretHint || "Sent as Authorization: Bearer <access token>"} disabled={saving} />
              <Field id="api-token-url" label="Token URL (optional)" value={form.token_url}
                onChange={set("token_url")} disabled={saving} />
            </>
          )}

          <div className="space-y-2">
            <Label className="text-gray-300 font-medium text-sm">Default headers (optional)</Label>
            <KeyValueEditor
              rows={form.headers}
              onChange={set("headers")}
              keyPlaceholder="Header"
              valuePlaceholder="Value"
              addLabel="Add header"
              disabled={saving}
            />
          </div>

          {(error || saveError) && <p className="text-sm text-red-400">{error || saveError}</p>}

          <DialogFooter className="pt-4 flex gap-3">
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" className={PRIMARY} disabled={saving}>
              {saving ? "Saving..." : editing ? "Save changes" : "Add connection"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
